import { Link } from "react-router-dom";
import { ArrowRight, Building, Briefcase } from "lucide-react";
import { motion } from "framer-motion";

const divisions = [
  {
    name: "RLK Technology Ltd",
    type: "Primary Operating Company",
    description: "Core technology development, product delivery and customer engagement. Holds principal IP and delivers contracted work.",
    activities: [
      "Product development and delivery",
      "Customer contracts and support",
      "Principal IP holding",
    ],
  },
  {
    name: "Helais Ltd",
    type: "Mission Systems Division",
    description: "Mission orchestration, aerial operations and the Helais platform. Delivers mission-systems solutions for public safety and protective security.",
    activities: [
      "Helais platform development",
      "Mission orchestration solutions",
      "Aerial operations systems",
    ],
  },
  {
    name: "UrbanAnalytix Ltd",
    type: "Analytics Division",
    description: "Operational intelligence from existing infrastructure. Delivers analytics solutions for transport, commercial and public space applications.",
    activities: [
      "UrbanAnalytix platform development",
      "Analytics solution delivery",
      "Data governance frameworks",
    ],
  },
];

export default function CompanyStructurePage() {
  return (
    <div className="relative">
      {/* Hero */}
      <section className="pt-32 pb-16 relative">
        <div className="absolute inset-0 grid-pattern opacity-50" />
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link to="/company" className="text-caption hover:text-foreground transition-colors mb-4 inline-block">
              Company
            </Link>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-12 w-12 rounded border border-border bg-muted/50 flex items-center justify-center">
                <Building className="h-6 w-6 text-muted-foreground" />
              </div>
            </div>
            <h1 className="text-display text-foreground mb-6">
              Our Companies
            </h1>
            <p className="text-subtitle max-w-2xl">
              The RLK Group operates through focused divisions, each with clear scope and accountability.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Master Brand */}
      <section className="section-padding">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl">
            <h2 className="text-headline text-foreground mb-6">
              The RLK Group
            </h2>
            <p className="text-body mb-4">
              The RLK Group is the master brand encompassing all operating companies and product lines. It represents our unified approach to operational intelligence and autonomy technologies.
            </p>
            <p className="text-body">
              Customers engage with The RLK Group for solutions. The underlying legal entities handle specific operational and contractual requirements.
            </p>
          </div>
        </div>
      </section>

      {/* Divisions */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="text-center mb-12">
            <p className="text-caption mb-4">Structure</p>
            <h2 className="text-headline text-foreground">Operating Companies</h2>
          </div>
          
          <div className="space-y-6 max-w-4xl mx-auto">
            {divisions.map((division, index) => (
              <motion.div
                key={index}
                className="card-pillar p-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-title text-foreground">{division.name}</h3>
                    <p className="text-sm text-muted-foreground">{division.type}</p>
                  </div>
                  <Briefcase className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-body mb-6">{division.description}</p>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Key Activities</p>
                  <ul className="space-y-1">
                    {division.activities.map((activity, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <div className="h-1 w-1 rounded-full bg-muted-foreground" />
                        <span className="text-sm text-muted-foreground">{activity}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Note */}
      <section className="section-padding-lg">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto">
            <div className="card-pillar p-8 border-l-2 border-l-muted-foreground">
              <h3 className="text-label text-foreground mb-4">Legal Structure</h3>
              <p className="text-body mb-4">
                All operating companies are registered in Scotland, UK. Specific contractual and legal matters are handled by the appropriate entity based on the nature of the engagement.
              </p>
              <p className="text-sm text-muted-foreground">
                For contractual enquiries, please contact info@rlktech.co.uk and we will direct you to the appropriate entity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding-lg bg-card/30 edge-glow">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-headline text-foreground mb-6">
              Work With Us
            </h2>
            <p className="text-body mb-8">
              Interested in partnering with The RLK Group? Tell us about your requirements and we will connect you with the right team.
            </p>
            <Link to="/contact" className="btn-primary">
              Get in Touch
              <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
